import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { insights } from "../data/content";
import { useReveal } from "../hooks/useReveal";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

export default function InsightPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = insights.find((p) => p.slug === slug);
  useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-paper text-ink flex flex-col items-center justify-center px-6">
        <div className="serif text-5xl font-light mb-4">Not found</div>
        <p className="text-slate mb-8">That essay doesn&apos;t exist.</p>
        <button
          onClick={() => navigate("/")}
          data-testid="insight-404-home"
          className="bg-ink text-paper px-6 py-3 text-sm hover:bg-moss transition-colors"
        >
          ← Back to home
        </button>
      </div>
    );
  }

  const otherPosts = insights.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid={`insight-page-${post.slug}`}>
      <Nav />

      <main className="pt-40 md:pt-48">
        <article>
          {/* Header */}
          <header className="px-6 md:px-12 pb-16 md:pb-24">
            <div className="max-w-[900px] mx-auto">
              <Link
                to="/#insights"
                data-testid="insight-back-link"
                className="overline text-slate hover:text-ink transition-colors inline-flex items-center gap-2 mb-12"
              >
                <span>←</span> All writing
              </Link>

              <div className="flex items-center gap-4 overline text-slate mb-8">
                <span>{post.date}</span>
                <span className="w-4 h-px bg-slate" />
                <span>{post.readTime}</span>
              </div>

              <h1
                data-testid="insight-title"
                className="serif text-4xl sm:text-5xl md:text-6xl lg:text-[64px] font-light tracking-tight leading-[1.05] text-ink"
              >
                {post.title}
              </h1>

              <p className="mt-10 serif italic text-2xl md:text-3xl text-moss leading-snug max-w-2xl">
                {post.dek}
              </p>
            </div>
          </header>

          <div className="rule max-w-[900px] mx-auto" />

          {/* Body */}
          <div className="px-6 md:px-12 py-14 md:py-20">
            <div
              className="max-w-[720px] mx-auto space-y-8 reveal"
              data-testid="insight-body"
            >
              {post.body.map((para, idx) => (
                <p
                  key={idx}
                  className={`text-lg md:text-xl leading-[1.7] ${
                    idx === 0
                      ? "text-ink first-letter:serif first-letter:text-6xl first-letter:font-light first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-none"
                      : "text-slate"
                  }`}
                >
                  {para}
                </p>
              ))}
            </div>
          </div>

          <div className="rule max-w-[900px] mx-auto" />

          {/* Author tag */}
          <div className="px-6 md:px-12 py-12">
            <div className="max-w-[720px] mx-auto flex items-center gap-5">
              <img
                src="/images/portrait.jpg"
                alt="Saran Gandhi"
                className="w-14 h-14 object-cover img-editorial"
              />
              <div>
                <div className="serif text-lg text-ink">Saran Gandhi</div>
                <div className="overline text-slate mt-1">Independent strategy &amp; operations consultant</div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA */}
        <section className="px-6 md:px-12 py-20 md:py-32 bg-ink text-paper">
          <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-8">
              <div className="overline text-paper/60 mb-6 reveal">Working on something similar?</div>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight reveal">
                Happy to talk it through —
                <span className="italic text-paper/70"> no deck.</span>
              </h2>
              <div className="mt-10 reveal">
                <a
                  href="https://calendly.com/sarangandhi1/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="insight-cta-calendly"
                  className="inline-flex items-center justify-center gap-3 bg-mustard text-ink px-7 py-4 text-sm tracking-wide hover:bg-paper hover:text-ink transition-colors duration-300 group"
                >
                  Book a 30-minute call
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Other posts */}
        {otherPosts.length > 0 && (
          <section className="px-6 md:px-12 py-20 md:py-32 border-t border-rule">
            <div className="max-w-[1400px] mx-auto">
              <div className="overline text-slate mb-12 reveal">Continue reading</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule">
                {otherPosts.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/insights/${p.slug}`}
                    data-testid={`related-insight-${p.slug}`}
                    className="group bg-paper p-8 md:p-12 hover:bg-white transition-colors duration-300 reveal"
                  >
                    <div className="overline text-slate mb-4">{p.date}</div>
                    <h3 className="serif text-2xl md:text-3xl font-light leading-tight text-ink group-hover:text-moss transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-4 text-sm text-slate leading-relaxed">
                      {p.dek}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
